import { ApiArticlesResponse, Article, useArticlesQuery } from '@/api/articles';
import ArticleItem from '../ArticleItem';
import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import equalArrays from 'lodash.isequal';
import { m, AnimatePresence } from 'framer-motion';
import CatalogPreloader from '../CatalogPreloader';
import Pagination from '@/components/shared/Pagination';
import animateScrollTo from 'animated-scroll-to';
import { unlockBodyScroll } from '@/utils/dom';
import { easeInOutCubic } from '@/utils/easings';

interface Props {
    articles: Article[];
    initialData: ApiArticlesResponse;
    initialQueryState: Record<string, any>;
}

const BlogList = ({ articles, initialData, initialQueryState }: Props) => {
    const router = useRouter();
    const wrapper = useRef<HTMLDivElement>(null);

    const [page, setPage] = useState(initialQueryState.page || 1);
    const [sort, setSort] = useState(initialQueryState.sort || undefined);
    const [order, setOrder] = useState(initialQueryState.order || undefined);
    const [tags, setTags] = useState<number[] | undefined>(
        initialQueryState['tags[]']?.map((item: number) => Number(item)),
    );

    const { data, isFetching } = useArticlesQuery({
        queryParams: {
            limit: 10,
            page,
            'tags[]': tags,
            order,
            sort,
        },
        initialData,
        callback: () => {
            unlockBodyScroll();
            if (wrapper.current) {
                animateScrollTo(wrapper.current, {
                    verticalOffset: -200,
                });
            }
        },
    });

    const pagesCount = data?.data?.totalCount ? Math.ceil(data.data.totalCount / 10) : 1;

    useEffect(() => {
        if (sort !== router.query.sort) {
            setSort(router.query.sort);
        }
        if (order !== router.query.order) {
            setOrder(router.query.order);
        }

        if (router.query['tags[]']) {
            if (
                Array.isArray(router.query['tags[]']) &&
                !equalArrays(
                    tags,
                    Array.from(router.query['tags[]']).map((item) => Number(item)),
                )
            ) {
                setTags(router.query['tags[]'].map((item) => Number(item)));
            }
            if (!Array.isArray(router.query['tags[]']) && !equalArrays(tags, [Number(router.query['tags[]'])])) {
                setTags([Number(router.query['tags[]'])]);
            }
        } else {
            if (tags && tags.length > 0) {
                setTags([]);
            }
        }

        if (page.toString() !== router.query.page && router.query.page) {
            setPage(router.query.page ? Math.max(1, Number(router.query.page)) : 1);
        }
    }, [router.query, order, sort, tags, page]);

    return (
        <div className="cards-list-with-pagination" ref={wrapper}>
            <AnimatePresence>{isFetching && <CatalogPreloader />}</AnimatePresence>
            <ul className="blog-list list-unstyled">
                <AnimatePresence>
                    {data?.data?.items.map((item, i) => {
                        return (
                            <m.li
                                key={item.id}
                                exit={{ opacity: 0 }}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{
                                    duration: 0.6,
                                    ease: easeInOutCubic,
                                }}
                                layout
                                className="blog-list__item"
                            >
                                <ArticleItem {...item} variant="list-item" />
                            </m.li>
                        );
                    })}
                </AnimatePresence>
            </ul>
            {pagesCount > 1 && (
                <Pagination
                    page={page}
                    length={pagesCount}
                    onPaginationChange={(number) => {
                        router.push(
                            { pathname: router.pathname, query: { ...router.query, page: number } },
                            undefined,
                            {
                                shallow: true,
                            },
                        );
                    }}
                />
            )}
        </div>
    );
};

export default BlogList;
